import React, { useState, useEffect } from 'react';
import { X, Star, Share2, Heart, Clock, ExternalLink, Crown, Sparkles, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LS = {
  LAST_UPSELL: 'mamalink_modal_upsell_last_shown',
  LAST_SHARE: 'mamalink_modal_share_last_shown',
  LAST_REVIEW: 'mamalink_modal_review_last_shown',
};

// Délai entre chaque affichage (en ms) - 1 SEMAINE
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Function to record social share and earn badge
const recordSocialShare = async (network) => {
  try {
    const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
    if (token) {
      await fetch(`${BACKEND_URL}/api/badges/social-share?network=${network}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    }
  } catch (error) {
    console.log('Error recording share:', error);
  }
};

export const ReminderModals = ({ userCreatedAt }) => {
  const navigate = useNavigate();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [userTier, setUserTier] = useState('free');

  // Review form state
  const [selectedRating, setSelectedRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    checkAndShowReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAndShowReminders = async () => {
    const now = Date.now();

    const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
    if (!token) return;

    const dayOfWeek = new Date().getDay();

    // Récupérer le tier de l'abonnement (et le statut review si Mercredi)
    let tier = 'free';
    let hasReviewed = false;
    try {
      const subRes = await fetch(`${BACKEND_URL}/api/subscription/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (subRes.ok) {
        const subData = await subRes.json();
        tier = subData.tier || 'free';
        setUserTier(tier);
      }

      if (dayOfWeek === 3) {
        const revRes = await fetch(`${BACKEND_URL}/api/reviews/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (revRes.ok) {
          const revData = await revRes.json();
          hasReviewed = !!revData.has_reviewed;
        }
      }
    } catch (e) {
      console.log('Error fetching subscription data:', e);
    }

    if (dayOfWeek === 1) {
      // Lundi : Upsell - jamais si Premium
      if (tier === 'premium') return;
      const last = parseInt(localStorage.getItem(LS.LAST_UPSELL) || '0', 10);
      if (now - last >= WEEK_MS) {
        localStorage.setItem(LS.LAST_UPSELL, now.toString());
        setTimeout(() => setShowUpsellModal(true), 2500);
      }
    } else if (dayOfWeek === 2) {
      // Mardi : Partage
      const last = parseInt(localStorage.getItem(LS.LAST_SHARE) || '0', 10);
      if (now - last >= WEEK_MS) {
        localStorage.setItem(LS.LAST_SHARE, now.toString());
        setTimeout(() => setShowShareModal(true), 2500);
      }
    } else if (dayOfWeek === 3) {
      // Mercredi : Avis PlayStore (tant que pas posté)
      if (hasReviewed) return;
      const last = parseInt(localStorage.getItem(LS.LAST_REVIEW) || '0', 10);
      if (now - last >= WEEK_MS) {
        localStorage.setItem(LS.LAST_REVIEW, now.toString());
        setTimeout(() => setShowReviewModal(true), 2500);
      }
    }
  };

  // ===== Handlers Upsell =====
  const handleUpsellLater = () => {
    setShowUpsellModal(false);
  };

  const handleUpsellNow = () => {
    setShowUpsellModal(false);
    navigate('/dashboard/premium');
  };

  // ===== Handlers Share =====
  const handleShareLater = () => setShowShareModal(false);

  const handleShareNow = async () => {
    const shareData = {
      title: 'MamaLink - La communauté des mamans',
      text: '👩‍👧‍👦 J\'ai rejoint MamaLink, une super communauté de mamans ! Rejoins-nous pour échanger, partager et rencontrer des mamans près de chez toi 💕',
      url: 'https://mamalink.fr'
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        alert('Lien copié ! Tu peux le coller sur tes réseaux sociaux 💕');
      }
    } catch (err) {
      console.log('Partage annulé');
    }
    setShowShareModal(false);
  };

  // ===== Handlers Review =====
  const handleReviewLater = () => {
    setShowReviewModal(false);
  };

  const handleSubmitReview = async () => {
    if (submittingReview) return;
    const trimmed = reviewComment.trim();
    if (trimmed.length < 5) {
      alert('Merci d\'écrire un petit message (au moins 5 caractères) 💕');
      return;
    }
    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      const res = await fetch(`${BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rating: selectedRating,
          comment: trimmed,
          category: 'playstore'
        })
      });
      if (!res.ok) throw new Error('failed');
      setReviewSubmitted(true);
      // Ouvrir le lien store
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const storeUrl = isIOS
        ? 'https://apps.apple.com/app/mamalink/id6753054000?action=write-review'
        : 'https://play.google.com/store/apps/details?id=fr.mamalink.app';
      window.open(storeUrl, '_blank');
      // Auto-close après 2s
      setTimeout(() => {
        setShowReviewModal(false);
        setReviewSubmitted(false);
      }, 2000);
    } catch (e) {
      alert('Une erreur est survenue. Réessaie plus tard 💕');
    } finally {
      setSubmittingReview(false);
    }
  };

  // ===================== Modal Upsell (Lundi) =====================
  if (showUpsellModal) {
    const isPlus = userTier === 'plus';
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden animate-scale-in" data-testid="upsell-modal">
          <div className="bg-gradient-to-br from-amber-300 via-amber-400 to-yellow-500 p-6 text-center relative">
            <button
              onClick={handleUpsellLater}
              className="absolute top-3 right-3 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              data-testid="upsell-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Crown className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-white font-bold text-xl">
              {isPlus ? 'Passe à Premium 👑' : 'Booste ton expérience ✨'}
            </h2>
          </div>

          <div className="p-6 text-center">
            <p className="text-[#5C3D36] mb-2 font-semibold">
              {isPlus
                ? 'Tu profites déjà de MamaLink+ !'
                : 'Tu mérites bien plus, jolie maman 💕'}
            </p>
            <p className="text-[#8B7355] text-sm mb-6">
              {isPlus
                ? 'Avec Premium, tu débloques les contacts illimités, les boosts, et tu vois qui visite ton profil 👑'
                : 'Avec MamaLink+ ou Premium, tu accèdes à des matchs illimités, des couleurs exclusives, et bien plus 💖'}
            </p>

            <div className="space-y-2 mb-6 text-left bg-amber-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
                <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span>Contacts privés illimités</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
                <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span>Boosts mensuels pour être vue</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#5C3D36]">
                <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span>Personnalisation avancée du chat</span>
              </div>
            </div>

            <button
              onClick={handleUpsellNow}
              className="w-full py-4 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 text-white rounded-2xl font-bold mb-3 flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
              data-testid="upsell-cta-btn"
            >
              <Crown className="w-5 h-5" />
              {isPlus ? 'Découvrir Premium' : 'Voir les abonnements'}
            </button>

            <button
              onClick={handleUpsellLater}
              className="w-full py-3 text-[#9D8189] font-medium flex items-center justify-center gap-2 hover:text-[#5C3D36] transition-colors"
              data-testid="upsell-later-btn"
            >
              <Clock className="w-4 h-4" />
              Plus tard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== Modal Partage (Mardi) =====================
  if (showShareModal) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden animate-scale-in" data-testid="share-modal">
          <div className="bg-gradient-to-br from-[#A2D2FF] to-[#BDE0FE] p-6 text-center relative">
            <button
              onClick={handleShareLater}
              className="absolute top-3 right-3 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              data-testid="share-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Share2 className="w-10 h-10 text-[#A2D2FF]" />
            </div>
            <h2 className="text-white font-bold text-xl">Partage l'amour ! 💕</h2>
          </div>

          <div className="p-6 text-center">
            <p className="text-[#5C3D36] mb-2">Tu aimes MamaLink ?</p>
            <p className="text-[#8B7355] text-sm mb-6">
              Fais découvrir notre communauté à tes amies mamans ! Plus on est de mamans, plus on s'entraide 🤗
            </p>

            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {/* Facebook */}
              <a href="https://www.facebook.com/sharer/sharer.php?u=https://mamalink.fr" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('facebook')} className="w-11 h-11 bg-[#1877F2] rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="Facebook" aria-label="Partager sur Facebook">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              {/* WhatsApp */}
              <a href="https://wa.me/?text=J'ai rejoint MamaLink, une super communauté de mamans ! Rejoins-nous 💕 https://mamalink.fr" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('whatsapp')} className="w-11 h-11 bg-[#25D366] rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="WhatsApp" aria-label="Partager sur WhatsApp">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              {/* Telegram */}
              <a href="https://t.me/share/url?url=https://mamalink.fr&text=J'ai rejoint MamaLink, une super communauté de mamans ! Rejoins-nous 💕" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('telegram')} className="w-11 h-11 bg-[#26A5E4] rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="Telegram" aria-label="Partager sur Telegram">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.464.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              </a>
              {/* Signal */}
              <a href="https://signal.me/" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('signal')} className="w-11 h-11 bg-[#3A76F0] rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="Signal" aria-label="Partager sur Signal">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="m9.12.35.27 1.09a10.845 10.845 0 0 0-3.015 1.248l-.578-.964A11.955 11.955 0 0 1 9.12.35zm5.76 0-.27 1.09a10.845 10.845 0 0 1 3.015 1.248l.578-.964A11.955 11.955 0 0 0 14.88.35zM1.725 5.797A11.955 11.955 0 0 0 .35 9.12l1.09.27A10.845 10.845 0 0 1 2.69 6.375zM1.125 12c0-.53.038-1.058.114-1.58L.148 10.27a12.096 12.096 0 0 0 0 3.458l1.09-.148A10.876 10.876 0 0 1 1.125 12zm17.51 9.532-.578-.964a10.845 10.845 0 0 1-2.985 1.235l.27 1.087a11.955 11.955 0 0 0 3.293-1.358zM22.875 12c0 .53-.038 1.058-.114 1.58l1.09.148a12.096 12.096 0 0 0 0-3.458l-1.09.148c.077.524.114 1.052.114 1.582zm.775 2.88-1.09-.27a10.845 10.845 0 0 1-1.235 2.985l.964.578a11.955 11.955 0 0 0 1.361-3.293zm-10.075 7.995a10.876 10.876 0 0 1-3.157 0l-.148 1.09a12.096 12.096 0 0 0 3.458 0zm7.008-3.305a10.906 10.906 0 0 1-2.232 2.233l.662.884a12.028 12.028 0 0 0 2.454-2.455zM19.24 6.375a10.844 10.844 0 0 1 1.248 3.015l1.09-.27a11.955 11.955 0 0 0-1.358-3.293zm-6.912 16.492c-.443.038-.887.038-1.33 0l-.147 1.09c.55.075 1.107.075 1.657 0zM12 1.125a10.876 10.876 0 0 1 1.58.114L13.73.148a12.096 12.096 0 0 0-3.458 0l.148 1.09A10.876 10.876 0 0 1 12 1.125zm-5.325 20.4-4.076 1.076 1.076-4.076-1.086-.288-1.075 4.075a1.125 1.125 0 0 0 1.376 1.375l4.075-1.075zm-4.29-3.171A10.845 10.845 0 0 1 1.152 15.37l-1.086.29a11.955 11.955 0 0 0 1.334 3.25zm6.792 3.464a10.845 10.845 0 0 1-3.107-1.088l-.503.99a11.955 11.955 0 0 0 3.44 1.19zM12 3.375A8.625 8.625 0 0 0 4.735 16.594L3.833 20l3.407-.902A8.625 8.625 0 1 0 12 3.375z"/></svg>
              </a>
              {/* TikTok */}
              <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('tiktok')} className="w-11 h-11 bg-black rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="TikTok" aria-label="Partager sur TikTok">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
              {/* X (Twitter) */}
              <a href="https://twitter.com/intent/tweet?text=J'ai rejoint MamaLink, une super communauté de mamans ! Rejoins-nous 💕&url=https://mamalink.fr" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('twitter')} className="w-11 h-11 bg-black rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="X (Twitter)" aria-label="Partager sur X">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#FFFFFF"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              {/* Instagram */}
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" onClick={() => recordSocialShare('instagram')} className="w-11 h-11 bg-gradient-to-br from-[#FEDA75] via-[#FA7E1E] via-40% via-[#D62976] via-70% to-[#4F5BD5] rounded-full flex items-center justify-center hover:scale-110 transition-transform" title="Instagram" aria-label="Partager sur Instagram">
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#FFFFFF"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
            </div>

            <button
              onClick={handleShareNow}
              className="w-full py-4 bg-gradient-to-r from-[#A2D2FF] to-[#BDE0FE] text-white rounded-2xl font-bold mb-3 flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
              data-testid="share-cta-btn"
            >
              <Share2 className="w-5 h-5" />
              Partager MamaLink
            </button>

            <button
              onClick={handleShareLater}
              className="w-full py-3 text-[#9D8189] font-medium flex items-center justify-center gap-2 hover:text-[#5C3D36] transition-colors"
              data-testid="share-later-btn"
            >
              <Clock className="w-4 h-4" />
              Me le rappeler plus tard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== Modal Avis PlayStore (Mercredi) =====================
  if (showReviewModal) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-3xl max-w-sm w-full overflow-hidden animate-scale-in" data-testid="review-modal">
          <div className="bg-gradient-to-br from-[#FFB5A7] to-[#FCD5CE] p-6 text-center relative">
            <button
              onClick={handleReviewLater}
              className="absolute top-3 right-3 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              data-testid="review-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Heart className="w-10 h-10 text-[#FFB5A7]" fill="#FFB5A7" />
            </div>
            <h2 className="text-white font-bold text-xl">Super Maman ! 💕</h2>
          </div>

          <div className="p-6 text-center">
            {reviewSubmitted ? (
              <div className="py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-[#5C3D36] font-bold mb-1">Merci pour ton avis ! 💕</p>
                <p className="text-sm text-[#8B7355]">Le Play Store s'ouvre maintenant pour finaliser…</p>
              </div>
            ) : (
              <>
                <p className="text-[#5C3D36] mb-2 font-semibold">Donne ton avis sur MamaLink</p>
                <p className="text-[#8B7355] text-sm mb-5">
                  Ton avis sera publié dans l'app et nous t'enverrons sur le Play Store 🌟
                </p>

                <div className="flex justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setSelectedRating(star)}
                      className="transition-transform hover:scale-110"
                      data-testid={`review-star-${star}`}
                    >
                      <Star
                        className={`w-9 h-9 transition-colors ${
                          star <= (hoverRating || selectedRating)
                            ? 'text-[#FFD700] fill-[#FFD700]'
                            : 'text-[#E8E0DC]'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Partage ton expérience en quelques mots…"
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#F8EDEB] focus:border-[#FFB5A7] focus:ring-2 focus:ring-[#FCD5CE] outline-none text-[#5C3D36] resize-none mb-4"
                  rows={3}
                  maxLength={500}
                  data-testid="review-comment-input"
                />

                <button
                  onClick={handleSubmitReview}
                  disabled={submittingReview}
                  className="w-full py-4 bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] text-white rounded-2xl font-bold mb-3 flex items-center justify-center gap-2 hover:shadow-lg transition-shadow disabled:opacity-60"
                  data-testid="review-submit-btn"
                >
                  {submittingReview ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Star className="w-5 h-5" />
                      Publier mon avis
                      <ExternalLink className="w-4 h-4" />
                    </>
                  )}
                </button>

                <button
                  onClick={handleReviewLater}
                  className="w-full py-3 text-[#9D8189] font-medium flex items-center justify-center gap-2 hover:text-[#5C3D36] transition-colors"
                  data-testid="review-later-btn"
                >
                  <Clock className="w-4 h-4" />
                  Me le rappeler plus tard
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ReminderModals;
